import { useRuntimeConfig, useScriptIntercom } from '#imports'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { getBrowserCookie } from '@backmarket/utils/cookies/getBrowserCookie'

import { useCareLogger } from '../../../composables/logger/useCareLogger'
import { isClient } from '../../../utils/isClient'
import translations from '../common/intercom.translations'
import { useGetJwtTokenOption } from '../composables/useGetJwtTokenOption'
import type { IntercomApi, IntercomLanguage } from '../types/intercom.types'

export function deleteIntercomCookie() {
  const { market } = useMarketplace()
  const { INTERCOM_APP_ID } = useRuntimeConfig().public

  const intercomCookieName = `intercom-session-${INTERCOM_APP_ID}`

  const intercomCookie = getBrowserCookie(intercomCookieName)

  if (intercomCookie) {
    document.cookie = `${intercomCookieName}=; Max-Age=0; path=/; domain=${market.domain};`
  }
}

export function withIntercomAvailabilityGuard() {
  const { logIntercomError } = useCareLogger()
  const i18n = useI18n()
  const { openErrorToast } = useTheToast()

  return ((event, ...args) => {
    if (!isClient()) {
      logIntercomError({
        errorTitle: '[Intercom] - Trying to call intercom method during SSR',
        errorDetail: {
          intercomMethodEvent: event,
          intercomMethodArgs: args,
          environement: 'server',
        },
      })

      return
    }

    if (window && typeof window.Intercom !== 'function') {
      openErrorToast({
        title: i18n(translations.toastErrorTitle),
        content: i18n(translations.toastErrorContent),
      })

      logIntercomError({
        errorTitle:
          '[Intercom] - Trying to call a method when intercom is not available',
        errorDetail: {
          intercomMethodEvent: event,
          intercomMethodArgs: args,
          environement: 'browser',
        },
      })

      return
    }

    window.Intercom(event, ...args)
  }) as IntercomApi
}

export function withEnhancedLoad(
  load: ReturnType<typeof useScriptIntercom>['load'],
) {
  const { logIntercomError } = useCareLogger()
  const i18n = useI18n()
  const { openErrorToast } = useTheToast()
  const { getJwtTokenOption } = useGetJwtTokenOption()

  return async () => {
    if (!isClient()) {
      logIntercomError({
        errorTitle: '[Intercom] - Trying to call manual load method during SSR',
        errorDetail: {
          intercomMethodEvent: 'load',
          environement: 'server',
        },
      })

      return
    }
    try {
      const response = await getJwtTokenOption()

      window.intercomSettings = {
        ...window.intercomSettings,
        intercom_user_jwt: response.jwt,
      }

      await load()
    } catch (error) {
      openErrorToast({
        title: i18n(translations.toastErrorTitle),
        content: i18n(translations.toastErrorContent),
      })
    }
  }
}

export function withEnhancedRemove(
  originalRemove?: ReturnType<typeof useScriptIntercom>['remove'],
) {
  if (originalRemove) {
    originalRemove()
  }

  const intercomLightWeightApp = document.getElementsByClassName(
    'intercom-lightweight-app',
  )
  const intercomIframe = document.getElementById('intercom-frame')

  const intercomContainer = document.getElementById('intercom-container')

  if (intercomLightWeightApp[0]) intercomLightWeightApp[0].remove()

  if (intercomIframe) intercomIframe.remove()

  if (intercomContainer) intercomContainer.remove()

  if ('Intercom' in window) {
    delete (window as unknown as Record<PropertyKey, unknown>).Intercom
  }

  if ('intercomSettings' in window) {
    delete (window as unknown as Record<PropertyKey, unknown>).intercomSettings
  }
}

// ATTENTION: When making changes, make sure to check the active languages in Intercom settings (ask CDIT)
export function bcp47LocaleToIntercomLocale(
  locale: ReturnType<typeof useI18nLocale>,
): IntercomLanguage {
  const localeMapping: Record<
    ReturnType<typeof useI18nLocale>,
    IntercomLanguage
  > = {
    'en-us': 'en',
    'es-us': 'es',
    'en-au': 'en',
    'en-gb': 'en',
    'fr-fr': 'fr',
    'de-de': 'de',
    'de-at': 'de',
    'es-es': 'es',
    'el-gr': 'el',
    'en-ie': 'en',
    'fi-fi': 'fi',
    'fr-be': 'fr',
    'it-it': 'it',
    'ja-jp': 'ja',
    'nl-nl': 'nl',
    'pt-pt': 'pt',
    'sv-se': 'sv',
    // Slovak is not supported by Intercom, fallback to English
    'sk-sk': 'en',
    'zh-hans': 'zh-CN',
  }

  return localeMapping[locale]
}
